<script setup lang="ts">
import { computed } from 'vue'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuGroup,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger
} from '@/components/shadcn'
import JKbd from '@/components/atoms/JKbd.vue'
import type { JContextMenuProps, ContextMenuItem as ContextMenuItemType } from '@/types/context-menu.types'

/**
 * JContextMenu 컴포넌트
 * 
 * shadcn-vue의 Context Menu를 래핑한 사용하기 쉬운 컨텍스트 메뉴 컴포넌트입니다.
 * 우클릭 시 나타나는 메뉴를 간단하게 구현할 수 있습니다.
 * 
 * @reference https://www.shadcn-vue.com/docs/components/context-menu.html
 * 
 * @emits select - 메뉴 아이템 선택 시
 * @emits open - 메뉴 열림
 * @emits close - 메뉴 닫힘
 */

const props = withDefaults(defineProps<JContextMenuProps>(), {
  items: () => [],
  disabled: false
})

const emit = defineEmits<{
  select: [itemId: string]
  open: []
  close: []
}>()

/**
 * 메뉴 구조 계산 - items를 그대로 사용
 */
const menuStructure = computed(() => {
  return props.items
})

/**
 * 아이템 클릭 핸들러
 */
const handleItemClick = (item: ContextMenuItemType) => {
  if (item.disabled) return
  
  emit('select', item.id)
  
  if (item.onClick) {
    item.onClick()
  }
}
</script>

<template>
  <ContextMenu>
    <ContextMenuTrigger :disabled="disabled" :class="props.class">
      <slot />
    </ContextMenuTrigger>
    
    <ContextMenuContent
      @pointer-down-outside="emit('close')"
      @escape-key-down="emit('close')"
      @focus-outside="emit('close')"
    >
      <template v-for="(group, groupIndex) in menuStructure" :key="groupIndex">
        <!-- 그룹 라벨 -->
        <ContextMenuLabel v-if="group.label">
          {{ group.label }}
        </ContextMenuLabel>
        
        <ContextMenuGroup>
          <template v-for="item in group.items" :key="item.id">
            <!-- 서브메뉴가 있는 경우 -->
            <ContextMenuSub v-if="item.children && item.children.length > 0">
              <ContextMenuSubTrigger :disabled="item.disabled">
                {{ item.label }}
              </ContextMenuSubTrigger>
              <ContextMenuSubContent>
                <template v-for="subItem in item.children" :key="subItem.id">
                  <!-- 일반 서브 아이템 -->
                  <ContextMenuItem
                    :disabled="subItem.disabled"
                    @click="handleItemClick(subItem)"
                  >
                    <slot name="item" :item="subItem">
                      {{ subItem.label }}
                    </slot>
                    <JKbd v-if="subItem.shortcut" size="sm" class="ml-auto">
                      {{ subItem.shortcut }}
                    </JKbd>
                  </ContextMenuItem>
                  <ContextMenuSeparator v-if="subItem.separator" />
                </template>
              </ContextMenuSubContent>
            </ContextMenuSub>

            <!-- 일반 아이템 -->
            <ContextMenuItem
              v-else
              :disabled="item.disabled"
              @click="handleItemClick(item)"
            >
              <slot name="item" :item="item">
                {{ item.label }}
              </slot>
              <JKbd v-if="item.shortcut" size="sm" class="ml-auto">
                {{ item.shortcut }}
              </JKbd>
            </ContextMenuItem>
            <ContextMenuSeparator v-if="item.separator" />
          </template>
        </ContextMenuGroup>
        
        <!-- 그룹 구분선 -->
        <ContextMenuSeparator v-if="groupIndex < menuStructure.length - 1" />
      </template>
    </ContextMenuContent>
  </ContextMenu>
</template>

<style scoped>
/* 추가 스타일이 필요한 경우 여기에 작성 */
</style>

